import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/Button';
import { ShieldCheck, Clock, AlertCircle, CheckCircle2 } from 'lucide-react';
import { OrderBump } from './OrderBump';
import { ExitIntentUpsell } from './ExitIntentUpsell';
function useCountdown(hours: number) {
  const [endTime] = useState(() => {
    const stored = localStorage.getItem('cta-countdown-end');
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (parsed > Date.now()) return parsed;
    }
    const end = Date.now() + hours * 60 * 60 * 1000;
    localStorage.setItem('cta-countdown-end', String(end));
    return end;
  });
  const [timeLeft, setTimeLeft] = useState(() => Math.max(0, endTime - Date.now()));
  useEffect(() => {
    const interval = setInterval(() => {
      const remaining = Math.max(0, endTime - Date.now());
      setTimeLeft(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [endTime]);
  const totalSeconds = Math.floor(timeLeft / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor(totalSeconds % 3600 / 60);
  const s = totalSeconds % 60;
  return {
    hours: h,
    minutes: m,
    seconds: s,
    isExpired: timeLeft <= 0
  };
}
const BASE_PRICE = 25000;
const BASE_REGULAR = 50000;
const BUMP_PRICE = 12500;
const BUMP_REGULAR = 50000;
function formatPrice(value: number) {
  return value.toLocaleString('es-AR');
}
const CART_URLS = {
  base: 'https://www.rutinavital.com/cart/47900606857460:1',
  baseSos: 'https://www.rutinavital.com/cart/48361067741428:1',
  baseSuper: 'https://www.rutinavital.com/cart/48361075867892:1',
  baseBoth: 'https://www.rutinavital.com/cart/48361068593396:1'
};
interface CTASectionProps {
  enableExitIntent?: boolean;
}
export function CTASection({
  enableExitIntent = false
}: CTASectionProps = {}) {
  const countdown = useCountdown(5);
  const [sosChecked, setSosChecked] = useState(false);
  const [superChecked, setSuperChecked] = useState(false);
  const [upsellOpen, setUpsellOpen] = useState(false);
  const bumpCount = (sosChecked ? 1 : 0) + (superChecked ? 1 : 0);
  const totalPrice = BASE_PRICE + bumpCount * BUMP_PRICE;
  const totalRegular = BASE_REGULAR + bumpCount * BUMP_REGULAR;
  const totalSavings = totalRegular - totalPrice;
  const savingsPercent = Math.round(totalSavings / totalRegular * 100);
  const cartUrl = sosChecked && superChecked ? CART_URLS.baseBoth : sosChecked ? CART_URLS.baseSos : superChecked ? CART_URLS.baseSuper : CART_URLS.base;
  const shouldInterceptCheckout = enableExitIntent && bumpCount === 0;
  const handleCheckoutClick = (e: React.MouseEvent) => {
    if (shouldInterceptCheckout) {
      e.preventDefault();
      setUpsellOpen(true);
    }
  };
  return <section id="cta-section" className="py-24 bg-white relative overflow-hidden" data-id="element-6">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-full bg-emerald-50/50 rounded-full blur-3xl -z-10" data-id="element-7"></div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-8">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl shadow-emerald-900/10 border border-emerald-100" data-id="element-9">
          <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-6" data-id="element-10">
            Recupera tu ligereza y tu confianza hoy mismo
          </h2>
          <p className="text-xl text-stone-600 mb-8" data-id="element-11">
            por una fracción de lo que cuesta una consulta nutricional privada.
          </p>

          {/* Countdown Timer */}
          {!countdown.isExpired && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="mb-8" data-id="element-12">
              <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-5 py-2 mb-4" data-id="element-13">
                <Clock className="w-4 h-4 text-amber-600" data-id="element-14" />
                <span className="text-sm font-semibold text-amber-800" data-id="element-15">
                  Oferta válida por tiempo limitado
                </span>
              </div>
              <div className="flex items-center justify-center gap-3" data-id="element-16">
                <div className="bg-stone-900 text-white rounded-xl px-4 py-3 min-w-[72px]" data-id="element-17">
                  <span className="text-2xl md:text-3xl font-bold tabular-nums" data-id="element-18">
                    {String(countdown.hours).padStart(2, '0')}
                  </span>
                  <span className="block text-xs text-stone-400 uppercase tracking-wider mt-1" data-id="element-19">
                    Horas
                  </span>
                </div>
                <span className="text-2xl font-bold text-stone-300" data-id="element-20">:</span>
                <div className="bg-stone-900 text-white rounded-xl px-4 py-3 min-w-[72px]" data-id="element-21">
                  <span className="text-2xl md:text-3xl font-bold tabular-nums" data-id="element-22">
                    {String(countdown.minutes).padStart(2, '0')}
                  </span>
                  <span className="block text-xs text-stone-400 uppercase tracking-wider mt-1" data-id="element-23">
                    Min
                  </span>
                </div>
                <span className="text-2xl font-bold text-stone-300" data-id="element-24">:</span>
                <div className="bg-stone-900 text-white rounded-xl px-4 py-3 min-w-[72px]" data-id="element-25">
                  <span className="text-2xl md:text-3xl font-bold tabular-nums" data-id="element-26">
                    {String(countdown.seconds).padStart(2, '0')}
                  </span>
                  <span className="block text-xs text-stone-400 uppercase tracking-wider mt-1" data-id="element-27">
                    Seg
                  </span>
                </div>
              </div>
            </motion.div>}

          {/* Pricing */}
          <div className="mb-10" data-id="element-28">
            <span className="block text-sm font-bold text-amber-500 uppercase tracking-wider mb-3" data-id="element-29">
              {bumpCount > 0 ? 'Tu pedido total' : 'Precio Especial de Lanzamiento'}
            </span>
            <div className="flex items-center justify-center gap-3 mb-2" data-id="element-30">
              <span className="text-2xl md:text-3xl text-stone-400 line-through font-semibold" data-id="element-31">
                ${formatPrice(totalRegular)}
              </span>
              <span className="bg-red-100 text-red-700 font-bold text-sm px-3 py-1 rounded-full" data-id="element-32">
                -{savingsPercent}% OFF
              </span>
            </div>
            <AnimatePresence mode="wait" data-id="element-33">
              <motion.div key={totalPrice} initial={{
              opacity: 0,
              scale: 0.92
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.92
            }} transition={{
              duration: 0.25
            }} className="text-5xl md:text-6xl font-extrabold text-emerald-600" data-id="element-34">
                ${formatPrice(totalPrice)}{' '}
                <span className="text-2xl text-stone-400 font-medium" data-id="element-35">ARS</span>
              </motion.div>
            </AnimatePresence>
            {bumpCount > 0 && <motion.p initial={{
            opacity: 0,
            y: -4
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-sm text-emerald-700 font-semibold mt-3" data-id="element-36">
                Estás ahorrando ${formatPrice(totalSavings)} con tu pedido
              </motion.p>}
          </div>

          <Button size="lg" href={cartUrl} onClick={handleCheckoutClick} className="w-full md:w-auto md:min-w-[320px] mb-3 text-lg py-5" data-id="element-37">
            QUIERO DESINFLAMARME AHORA · ${formatPrice(totalPrice)}
          </Button>

          {/* Urgency text below button */}
          <p className="text-xs text-stone-500 mb-8 flex items-center justify-center gap-1.5" data-id="element-38">
            <AlertCircle className="w-3.5 h-3.5 text-amber-500" data-id="element-39" />
            Esta oferta no volverá a aparecer después de este paso
          </p>

          {/* ORDER BUMPS */}
          <div className="space-y-5 mb-8" data-id="element-40">
            <OrderBump variant="sos" checked={sosChecked} onToggle={() => setSosChecked(!sosChecked)} title='¿Tenés un evento pronto o te sentís "explotar" hoy? Agregá el Reset de Emergencia por solo $12.500' description='No esperes 30 días para ver los primeros cambios. Si tenés una fiesta, una reunión importante o simplemente el fin de semana fue pesado y necesitás resultados para mañana, el Protocolo SOS 24hs es tu "botón de pánico". Te entregamos la hoja de ruta exacta para drenar líquidos y desinflamar el abdomen de un día para el otro de forma segura.' features={['La Infusión Maestra: 3 ingredientes para deshinchar el intestino en horas.', 'Menú de Emergencia: Las 4 comidas clave para "secar" el cuerpo.', 'Rutina Linfática Express: 3 movimientos de 2 minutos para activar el drenaje.', 'Checklist de Acción: Qué sacar hoy mismo para amanecer liviana.']} priceLine="SÍ, sumalo a mi pedido por $12.500 (regular $50.000)" imageUrl="https://cdn.magicpatterns.com/uploads/osQ2sk75rcQ3HuJPTfMrVz/order_bump_1.png" imageAlt="Protocolo SOS 24hs" data-id="element-41" />

            <OrderBump variant="super" checked={superChecked} onToggle={() => setSuperChecked(!superChecked)} title="¡No tires plata en el súper! Sumá la Guía de Compras Inteligente por solo $12.500" description='Comer sano en Argentina no tiene por qué ser caro ni requerir ir a dietéticas boutique. El secreto está en saber qué elegir en el Coto, Carrefour o el Chino de la vuelta. Con esta guía, vas a dejar de caer en las trampas del marketing "Fit" que en realidad te inflama y vas a aprender a llenar el changuito con comida real, barata y efectiva.' features={['Semáforo de Productos: Marcas recomendadas y cuáles evitar a toda costa.', 'Lista de Compras "Bajo Presupuesto": Comé increíble por menos de lo que gastás hoy.', 'El Detector de Mentiras: Cómo leer etiquetas en 3 segundos (JMAF, Aceites, Aditivos).', 'Hack de la Despensa: Los 5 básicos que nunca te pueden faltar para no fallar.']} priceLine="SÍ, sumalo a mi pedido por solo $12.500" imageUrl="https://cdn.magicpatterns.com/uploads/sf1VvmngpwJU7SwfbtxYsT/Order_Bump_2.png" imageAlt="El Súper Anti-Inflamatorio" data-id="element-42" />
          </div>

          <div className="flex items-center justify-center text-sm text-stone-500 mt-6" data-id="element-43">
            <ShieldCheck className="w-5 h-5 text-emerald-500 mr-2" data-id="element-44" />
            <span data-id="element-45">
              Pago seguro mediante Mercado Pago o Tarjeta de Crédito. Acceso
              instantáneo.
            </span>
          </div>

          {/* Order Summary - what you're getting */}
          <div className="mt-8 bg-stone-50 border border-stone-200 rounded-2xl p-6 text-left" data-id="element-46">
            <h4 className="text-sm font-bold uppercase tracking-wider text-stone-700 mb-4 text-center" data-id="element-47">
              Tu pedido incluye:
            </h4>
            <ul className="space-y-2.5" data-id="element-48">
              <li className="flex items-start justify-between gap-3 text-sm text-stone-700" data-id="element-49">
                <div className="flex items-start gap-3 flex-1 min-w-0" data-id="element-50">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" data-id="element-51" />
                  <span data-id="element-52">
                    <strong className="text-stone-900" data-id="element-53">
                      Protocolo Anti-Inflamación
                    </strong>{' '}
                    — Plan 30 días + Reset 72hs
                  </span>
                </div>
                <span className="text-stone-600 font-semibold whitespace-nowrap shrink-0" data-id="element-54">
                  $25.000
                </span>
              </li>
              <li className="flex items-start justify-between gap-3 text-sm text-stone-700" data-id="element-55">
                <div className="flex items-start gap-3 flex-1 min-w-0" data-id="element-56">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" data-id="element-57" />
                  <span data-id="element-58">
                    <strong className="text-stone-900" data-id="element-59">Bono #1:</strong> Menú
                    Anti-Inflamación Express (15 Min)
                  </span>
                </div>
                <div className="flex items-center gap-2 whitespace-nowrap shrink-0" data-id="element-60">
                  <span className="line-through text-stone-400 text-xs" data-id="element-61">
                    $50.000
                  </span>
                  <span className="bg-emerald-100 text-emerald-700 font-bold text-xs px-2 py-0.5 rounded-full" data-id="element-62">
                    GRATIS
                  </span>
                </div>
              </li>
              <li className="flex items-start justify-between gap-3 text-sm text-stone-700" data-id="element-63">
                <div className="flex items-start gap-3 flex-1 min-w-0" data-id="element-64">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" data-id="element-65" />
                  <span data-id="element-66">
                    <strong className="text-stone-900" data-id="element-67">Bono #2:</strong> Tracker
                    "Abdomen Calmado" (7 Días)
                  </span>
                </div>
                <div className="flex items-center gap-2 whitespace-nowrap shrink-0" data-id="element-68">
                  <span className="line-through text-stone-400 text-xs" data-id="element-69">
                    $50.000
                  </span>
                  <span className="bg-emerald-100 text-emerald-700 font-bold text-xs px-2 py-0.5 rounded-full" data-id="element-70">
                    GRATIS
                  </span>
                </div>
              </li>
              <li className="flex items-start justify-between gap-3 text-sm text-stone-700" data-id="element-71">
                <div className="flex items-start gap-3 flex-1 min-w-0" data-id="element-72">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" data-id="element-73" />
                  <span data-id="element-74">
                    <strong className="text-stone-900" data-id="element-75">Bono #3:</strong> Guía
                    "Café Sin Inflamar" (En 5 Reglas)
                  </span>
                </div>
                <div className="flex items-center gap-2 whitespace-nowrap shrink-0" data-id="element-76">
                  <span className="line-through text-stone-400 text-xs" data-id="element-77">
                    $50.000
                  </span>
                  <span className="bg-emerald-100 text-emerald-700 font-bold text-xs px-2 py-0.5 rounded-full" data-id="element-78">
                    GRATIS
                  </span>
                </div>
              </li>

              <AnimatePresence data-id="element-79">
                {sosChecked && <motion.li key="sos-summary" initial={{
                opacity: 0,
                height: 0,
                marginTop: 0
              }} animate={{
                opacity: 1,
                height: 'auto',
                marginTop: 10
              }} exit={{
                opacity: 0,
                height: 0,
                marginTop: 0
              }} transition={{
                duration: 0.3
              }} className="flex items-start justify-between gap-3 text-sm text-stone-700 overflow-hidden" data-id="element-80">
                    <div className="flex items-start gap-3 flex-1 min-w-0" data-id="element-81">
                      <CheckCircle2 className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" data-id="element-82" />
                      <span data-id="element-83">
                        <strong className="text-stone-900" data-id="element-84">Extra:</strong>{' '}
                        Protocolo SOS 24hs
                      </span>
                    </div>
                    <div className="flex items-center gap-2 whitespace-nowrap shrink-0" data-id="element-85">
                      <span className="line-through text-stone-400 text-xs" data-id="element-86">
                        $50.000
                      </span>
                      <span className="text-amber-700 font-bold text-sm" data-id="element-87">
                        $12.500
                      </span>
                    </div>
                  </motion.li>}
                {superChecked && <motion.li key="super-summary" initial={{
                opacity: 0,
                height: 0,
                marginTop: 0
              }} animate={{
                opacity: 1,
                height: 'auto',
                marginTop: 10
              }} exit={{
                opacity: 0,
                height: 0,
                marginTop: 0
              }} transition={{
                duration: 0.3
              }} className="flex items-start justify-between gap-3 text-sm text-stone-700 overflow-hidden" data-id="element-88">
                    <div className="flex items-start gap-3 flex-1 min-w-0" data-id="element-89">
                      <CheckCircle2 className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" data-id="element-90" />
                      <span data-id="element-91">
                        <strong className="text-stone-900" data-id="element-92">Extra:</strong> El
                        Súper Anti-Inflamatorio
                      </span>
                    </div>
                    <div className="flex items-center gap-2 whitespace-nowrap shrink-0" data-id="element-93">
                      <span className="line-through text-stone-400 text-xs" data-id="element-94">
                        $50.000
                      </span>
                      <span className="text-amber-700 font-bold text-sm" data-id="element-95">
                        $12.500
                      </span>
                    </div>
                  </motion.li>}
              </AnimatePresence>
            </ul>

            {/* Subtotal regular value (crossed out) */}
            <div className="mt-5 pt-4 border-t border-stone-200 flex items-center justify-between text-sm" data-id="element-96">
              <span className="text-stone-500" data-id="element-97">Valor total real:</span>
              <span className="line-through text-stone-400 font-medium" data-id="element-98">
                ${formatPrice(200000 + bumpCount * 50000)}
              </span>
            </div>

            {/* Discount badge */}
            <div className="mt-2 flex items-center justify-between text-sm" data-id="element-99">
              <span className="text-stone-700 font-semibold" data-id="element-100">
                Tu descuento:
              </span>
              <span className="bg-red-100 text-red-700 font-bold text-sm px-3 py-1 rounded-full" data-id="element-101">
                -80% OFF
              </span>
            </div>

            {/* Final total */}
            <div className="mt-4 pt-4 border-t-2 border-emerald-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-102">
              <div className="flex items-center justify-between sm:justify-start gap-3 sm:flex-1" data-id="element-103">
                <span className="text-base font-bold text-stone-900" data-id="element-104">
                  TOTAL A PAGAR HOY:
                </span>
                <AnimatePresence mode="wait" data-id="element-105">
                  <motion.span key={totalPrice} initial={{
                  opacity: 0,
                  y: -4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 4
                }} transition={{
                  duration: 0.2
                }} className="text-2xl font-extrabold text-emerald-600" data-id="element-106">
                    ${formatPrice(totalPrice)} ARS
                  </motion.span>
                </AnimatePresence>
              </div>
              <Button href={cartUrl} onClick={handleCheckoutClick} className="whitespace-nowrap" data-id="element-107">
                Comprar ahora
              </Button>
            </div>
          </div>
        </motion.div>
      </div>

      <ExitIntentUpsell open={upsellOpen} onClose={() => setUpsellOpen(false)} bundleUrl="https://rutinavital.com/cart/48370765955316:1" mainOnlyUrl="https://rutinavital.com/cart/47900606857460:1" data-id="element-108" />
    </section>;
}